// Pure helpers behind "ส่งเข้า seller" on /admin/product_platform (mdf-bo-fn#260,
// spec docs/specs/2026-09-22-shopee-master-product-sync-design.md §6A).
//
// The flow is: a Shopee row -> modal -> POST
// /api/platform/v2/shopee/products/:item_id/to-seller with {platform_shop_id,
// category_id, dry_run:true} (preview) and then the same call with
// dry_run:false (write). Everything in this module is Vue-free / axios-free on
// purpose: PlatformProductTableV2.vue cannot be mounted under this repo's jest
// setup (see tests/unit/skuWmsOptionsSource.spec.js), so the rules that decide
// WHICH rows get the button, HOW reject codes are shown and HOW the badge
// lookup is batched live here where they can be unit-tested directly.

// --------------------------------------------------------------------------
// THE BACKEND CONTRACT — every response shape POST .../to-seller can answer
// with. Read off controllers/PlatformShopeeToSeller.go (mdf-bo-api#576); the
// line numbers are that file unless stated otherwise. Anything mapped below is
// mapped on THESE bytes, and tests/unit/shopeeToSeller.spec.js uses them as
// fixtures verbatim — a made-up body there would test nothing.
//
//   200  {dry_run, action:"create"|"update", product_id, product:{name,
//        image_count, description_image_count, option_groups[]},
//        skus:[{model_id, sku_id, sku_code, sku_name, price, action}],
//        warnings:[{code, message}]}                     :186-196 (dry run)
//                                                        :408-415 (real run)
//        `warnings` is present in BOTH — the dry run may report wms_unmatched
//        (and after the real run, image_import_failed as well), so the preview
//        renders warnings on either run.
//
//   400  {"code":"category_required",
//         "message":"category_id is required when the product does not exist
//                    in the seller catalog yet"}                      :169-174
//        NOTE: this one does NOT use the jsonErr envelope — there is no Msg
//        field to read, which is why the mapping keys off `code`.
//
//   400  {"Status":"error","Msg":"invalid item_id"}                       :111
//   400  {"Status":"error","Msg":"invalid request body"}                  :116
//   400  {"Status":"error","Msg":"platform_shop_id is required"}          :123
//
//   422  {"rejects":[{"model_id":0,"reason_code":"…","message":"…"}]}  :158-161
//        reason_code vocabulary: services/shopeeseller/shopeeseller.go:35-40
//        (+ model_list_empty). ONE reject = nothing was written.
//
//   500  {"Status":"error","Msg":"…"}      :132 :138 :202 :282 :313 :320 :324
//                                          :364 :375 :382 :391 :396
//
//   502  {"Status":"error","Msg":"<shopee fetch error>"}                  :147
//   502  {"Status":"error","Msg":"<payload parse error>"}                 :156
//   502  {"Status":"error","Msg":"the cover image could not be imported;
//         nothing was written"}                                          :209
//        Three different 502s: only the last one means "the import itself
//        failed on our side", so the status alone must not be read as the
//        cover-image case.
//
//   No 404 and no 409 exist on this route: an item that is not linked yet is a
//   `create`, not a miss, and a concurrent second import is resolved by the
//   link table's ON CONFLICT (:815, :840) rather than being refused.
//
//   The envelope {"Status":"error","Msg":…} is jsonErr, controllers/
//   Member_auth.go:722.
//
//   GET .../seller-links answers 200 {"links":[{item_id, product_id,
//   last_synced_at}]} :467-476, and jsonErr 400/500 otherwise (:424 :435 :441
//   :448 :464).
// --------------------------------------------------------------------------

// SHOPEE_PLATFORM_ID is the platform id used by the Backoffice for Shopee. It
// is the same literal the page's PlatformTable already carries ({id:3,
// name:"Shopee"}) and the same one the BE uses (controllers Platform enum).
// The button exists ONLY for these rows: a TikTok/Lazada/MDFStore/Makro item
// has no Shopee item_id to send.
export const SHOPEE_PLATFORM_ID = 3;

// Shopee item_status values the owner allows sending (spec §2, last row):
// NORMAL and UNLIST. BANNED / DELETED are refused by the API (422
// item_status_not_allowed), so the UI disables the button up front with a
// reason instead of letting the user discover it after a round trip.
export const ALLOWED_ITEM_STATUSES = ["NORMAL", "UNLIST"];

// isShopeeRow: is this a row the button belongs on?
//
// platform_id arrives as a number from /api/v3/products, but the page's own
// filters pass it around as a select value and older rows have been seen as
// strings, so compare numerically rather than with ===.
export function isShopeeRow(row) {
  if (!row || typeof row !== "object") return false;
  return Number(row.platform_id) === SHOPEE_PLATFORM_ID;
}

// shopeeItemStatus digs the Shopee-native listing status out of a row.
//
// IMPORTANT: the BO product row (DBProduct) has NO Shopee item_status column —
// its `status` is the Backoffice 1=active / 2=inactive flag, which is a
// different thing and must never be read as NORMAL/UNLIST/BANNED. So this
// looks only at fields that would carry a real Shopee status if the list
// endpoint ever exposes one, and returns "" when there is none. Callers treat
// "" as "unknown -> leave the button enabled", because the API enforces the
// rule anyway (422 item_status_not_allowed).
export function shopeeItemStatus(row) {
  if (!row || typeof row !== "object") return "";
  const candidates = [row.item_status, row.shopee_status, row.platform_status];
  for (const c of candidates) {
    if (c === null || c === undefined) continue;
    const s = String(c).trim();
    if (s !== "") return s.toUpperCase();
  }
  return "";
}

// isItemStatusSendable: NORMAL/UNLIST -> true, BANNED/DELETED -> false,
// unknown ("") -> true (see shopeeItemStatus).
export function isItemStatusSendable(row) {
  const status = shopeeItemStatus(row);
  if (status === "") return true;
  return ALLOWED_ITEM_STATUSES.indexOf(status) !== -1;
}

// sendToSellerDisabledReason returns the i18n key for the tooltip when the
// button must be disabled, or "" when the row can be sent. Only ever called
// for rows that already passed isShopeeRow.
export function sendToSellerDisabledReason(row) {
  if (!isShopeeRow(row)) return "shopee_to_seller.disabled_not_shopee";
  if (!isItemStatusSendable(row)) return "shopee_to_seller.disabled_item_status";
  if (!shopeeItemId(row)) return "shopee_to_seller.disabled_no_item_id";
  return "";
}

// shopeeItemId / shopeeShopId: the two identifiers the endpoint needs.
// item_id is the Shopee listing id, stored on the BO row as
// `platform_product_id` (a string column); platform_shop_id is the numeric
// shop id on the same row. Both are returned as strings/numbers exactly as the
// API expects and as "" / 0 when absent so a caller can guard cheaply.
export function shopeeItemId(row) {
  if (!row || typeof row !== "object") return "";
  const raw = row.platform_product_id;
  if (raw === null || raw === undefined) return "";
  return String(raw).trim();
}

export function shopeeShopId(row) {
  if (!row || typeof row !== "object") return 0;
  const n = Number(row.platform_shop_id);
  if (!isFinite(n) || n <= 0) return 0;
  return n;
}

// --------------------------------------------------------------------------
// reject codes (422)
// --------------------------------------------------------------------------

// REJECT_REASON_CODES are the codes the BE emits (spec §6A, type Reject —
// services/shopeeseller/shopeeseller.go):
//   model_sku_empty         — a variation has no model_sku; the WHOLE item is
//                             refused, nothing is written
//   sku_code_too_long       — the SKU code exceeds the seller column width
//   item_status_not_allowed — item_status outside NORMAL/UNLIST
//   no_price                — the model carries no usable price
//   model_list_empty        — the item says it has variations but Shopee's
//                             model list came back empty; importing it would
//                             produce a product with no SKU at all
export const REJECT_REASON_CODES = [
  "model_sku_empty",
  "sku_code_too_long",
  "item_status_not_allowed",
  "no_price",
  "model_list_empty"
];

// rejectReasonI18nKey maps a reason_code to a key under `shopee_to_seller.reject.*`.
// An unknown / missing code falls back to `shopee_to_seller.reject.unknown` so a
// new BE code shows a sentence instead of a raw identifier or a blank row.
export function rejectReasonI18nKey(code) {
  const c = code === null || code === undefined ? "" : String(code).trim();
  if (REJECT_REASON_CODES.indexOf(c) === -1) return "shopee_to_seller.reject.unknown";
  return "shopee_to_seller.reject." + c;
}

// rejectCode normalises the reason_code off one entry of the 422 `rejects[]`.
function rejectCode(reject) {
  if (!reject || typeof reject !== "object") return "";
  const raw = reject.reason_code === null || reject.reason_code === undefined
    ? reject.ReasonCode
    : reject.reason_code;
  return raw === null || raw === undefined ? "" : String(raw).trim();
}

function rejectMessage(reject) {
  if (!reject || typeof reject !== "object") return "";
  const raw = reject.message === null || reject.message === undefined
    ? reject.Message
    : reject.message;
  return raw === null || raw === undefined ? "" : String(raw).trim();
}

// rejectDisplayText is the LINE the modal prints for one reject.
//
// A known code is shown translated. An UNKNOWN code — the BE adding a sixth
// reason before this screen learns about it, which is exactly how
// model_list_empty arrived — falls back to the backend's own `message`, so the
// operator reads why Shopee refused the item instead of a generic sentence or,
// worse, a blank bullet. Only when there is no message either does the generic
// `reject.unknown` sentence stand in.
//
// `translate` is the caller's $t; it is optional so the function stays testable
// without a Vue instance (it then returns the key itself).
export function rejectDisplayText(reject, translate) {
  const t = typeof translate === "function" ? translate : (k) => k;
  const code = rejectCode(reject);
  if (REJECT_REASON_CODES.indexOf(code) !== -1) {
    return t("shopee_to_seller.reject." + code);
  }
  const message = rejectMessage(reject);
  if (message !== "") return message;
  return t("shopee_to_seller.reject.unknown");
}

// rejectDetailText is the grey suffix after the translated sentence: the
// backend's message, but ONLY for a code we translated ourselves — for an
// unknown code rejectDisplayText already printed the message and repeating it
// would read as a stutter.
export function rejectDetailText(reject) {
  if (REJECT_REASON_CODES.indexOf(rejectCode(reject)) === -1) return "";
  return rejectMessage(reject);
}

// --------------------------------------------------------------------------
// SKU action tags
// --------------------------------------------------------------------------

// SKU_ACTION_TAG_TYPES maps the per-SKU action from the preview to an el-tag
// `type`. Element UI 2.4.11 ships success / info / warning / danger (plus the
// default primary), so:
//   create     -> success (new row)
//   update     -> info    (existing row, fields overwritten)
//   deactivate -> danger  (is_active=false; the SKU vanished from Shopee)
//   reactivate -> warning (it came back)
export const SKU_ACTION_TAG_TYPES = {
  create: "success",
  update: "info",
  deactivate: "danger",
  reactivate: "warning"
};

export function skuActionTagType(action) {
  const a = action === null || action === undefined ? "" : String(action).trim();
  return SKU_ACTION_TAG_TYPES[a] || "info";
}

export function skuActionI18nKey(action) {
  const a = action === null || action === undefined ? "" : String(action).trim();
  if (!Object.prototype.hasOwnProperty.call(SKU_ACTION_TAG_TYPES, a)) {
    return "shopee_to_seller.sku_action.unknown";
  }
  return "shopee_to_seller.sku_action." + a;
}

// --------------------------------------------------------------------------
// seller-links badge lookup
// --------------------------------------------------------------------------

// SELLER_LINKS_MAX_IDS is the cap the endpoint documents for `item_ids`
// (spec §6A HTTP table: "?platform_shop_id=&item_ids=1,2,3 (≤200)"). A page of
// the table is normally far below this, but the grouping below chunks anyway so
// a 500-row page can never produce a rejected request.
export const SELLER_LINKS_MAX_IDS = 200;

// buildSellerLinkRequests turns the rows of ONE loaded page into the minimum
// set of GET /seller-links calls: Shopee rows only, grouped by
// platform_shop_id (the endpoint takes a single shop per call), de-duplicated
// on item_id, and chunked at SELLER_LINKS_MAX_IDS.
//
// Returns [{ platform_shop_id: Number, item_ids: [String] }] with a stable
// order (shops in first-seen order, ids in first-seen order) so the specs can
// assert on it and so a re-render issues the same calls.
export function buildSellerLinkRequests(rows, maxIds) {
  const cap = maxIds === null || maxIds === undefined ? SELLER_LINKS_MAX_IDS : Number(maxIds);
  const limit = isFinite(cap) && cap > 0 ? Math.floor(cap) : SELLER_LINKS_MAX_IDS;
  if (!Array.isArray(rows)) return [];

  const order = [];
  const byShop = {};
  for (const row of rows) {
    if (!isShopeeRow(row)) continue;
    const shopId = shopeeShopId(row);
    const itemId = shopeeItemId(row);
    if (!shopId || itemId === "") continue;
    if (!Object.prototype.hasOwnProperty.call(byShop, shopId)) {
      byShop[shopId] = { seen: {}, ids: [] };
      order.push(shopId);
    }
    const bucket = byShop[shopId];
    if (bucket.seen[itemId]) continue;
    bucket.seen[itemId] = true;
    bucket.ids.push(itemId);
  }

  const requests = [];
  for (const shopId of order) {
    const ids = byShop[shopId].ids;
    for (let i = 0; i < ids.length; i += limit) {
      requests.push({
        platform_shop_id: Number(shopId),
        item_ids: ids.slice(i, i + limit)
      });
    }
  }
  return requests;
}

// indexSellerLinks folds the `links` arrays of the responses into a lookup
// keyed by item_id (as a string — JS object keys are strings and item ids
// exceed 2^53 on Shopee, so they are carried as strings everywhere here).
//
// Accepts a flat array of link objects or an array of arrays (one per call).
// Later entries win, which is what a post-sync refresh wants.
export function indexSellerLinks(payloads) {
  const index = {};
  const push = (link) => {
    if (!link || typeof link !== "object") return;
    const itemId = link.item_id === null || link.item_id === undefined ? "" : String(link.item_id).trim();
    if (itemId === "") return;
    index[itemId] = {
      product_id: Number(link.product_id) || 0,
      last_synced_at: link.last_synced_at || ""
    };
  };
  const walk = (value) => {
    if (!Array.isArray(value)) return;
    for (const entry of value) {
      if (Array.isArray(entry)) walk(entry);
      else push(entry);
    }
  };
  walk(payloads);
  return index;
}

// sellerLinkForRow: the badge's data for one row, or null when the row has no
// link yet (no badge).
export function sellerLinkForRow(index, row) {
  if (!index || typeof index !== "object") return null;
  const itemId = shopeeItemId(row);
  if (itemId === "") return null;
  const link = index[itemId];
  return link || null;
}

// --------------------------------------------------------------------------
// request bodies
// --------------------------------------------------------------------------

// toSellerBody builds the POST body. `dry_run` is explicit at every call site
// on purpose — the modal must send true (ตรวจสอบ) before it may send false
// (ยืนยัน), and a spec asserts exactly that ordering.
//
// category_id is omitted when falsy: on an update the field must not be sent
// (sending it does not change the category, spec §6A), and on a create the BE
// answers 400 category_required, which the modal surfaces.
export function toSellerBody(shopId, categoryId, dryRun) {
  const body = {
    // STRING, not number: the Go handler binds this into a `string` field and
    // answers 400 "invalid request body" to a JSON number.
    platform_shop_id: String(Number(shopId) || ""),
    dry_run: dryRun === true
  };
  const cat = Number(categoryId);
  if (isFinite(cat) && cat > 0) body.category_id = cat;
  return body;
}

// errorBodyCode reads the machine-readable code off an error body.
//
// The handler's ONLY coded error is the 400 at :169-174, and it is NOT the
// jsonErr envelope: it carries {code, message} and no Msg at all. Reading the
// code is therefore the only way to recognise it — looking for the substring
// "category_required" inside Msg/message never matched, because `code` is where
// the identifier lives and `message` is the English sentence. Code / error_code
// are tolerated for a proxy that re-cases or re-names the field.
function errorBodyCode(data) {
  if (!data || typeof data !== "object") return "";
  const raw = data.code || data.Code || data.error_code || data.errorCode;
  return raw === null || raw === undefined ? "" : String(raw).trim();
}

// errorBodyMessage reads the human text: jsonErr's Msg (Member_auth.go:722),
// or `message` for the coded 400.
function errorBodyMessage(data) {
  if (!data || typeof data !== "object") return "";
  const raw = data.Msg || data.msg || data.message || data.Message || data.error;
  return raw === null || raw === undefined ? "" : String(raw).trim();
}

// COVER_IMAGE_FAILED_MSG is the sentence the handler sends with the ONE 502
// that means "the import stopped because the cover image could not be moved"
// (:209). The other two 502s (:147 Shopee fetch, :156 payload parse) are
// upstream failures and must not claim a cover image was involved, so the
// status alone is not enough to pick the message.
export const COVER_IMAGE_FAILED_MSG = "the cover image could not be imported; nothing was written";

// toSellerErrorI18nKey maps an axios error to the message key the modal shows.
// Every branch matches a body listed in the contract block at the top:
//   400 {code:"category_required"}        -> ask for a category
//   400 {Status,Msg}                      -> generic bad request
//   422 {rejects:[…]}                     -> the list is rendered instead
//   502 cover-image sentence              -> cover_image_failed
//   502 anything else                     -> upstream (Shopee / payload)
//   everything else (incl. 500)           -> generic
export function toSellerErrorI18nKey(error) {
  const status = error && error.response ? Number(error.response.status) : 0;
  const data = error && error.response ? error.response.data : null;
  if (status === 400) {
    if (errorBodyCode(data) === "category_required") {
      return "shopee_to_seller.error.category_required";
    }
    return "shopee_to_seller.error.bad_request";
  }
  if (status === 502) {
    const msg = errorBodyMessage(data);
    if (msg && msg.indexOf(COVER_IMAGE_FAILED_MSG) !== -1) {
      return "shopee_to_seller.error.cover_image_failed";
    }
    return "shopee_to_seller.error.upstream";
  }
  if (status === 422) return "shopee_to_seller.error.rejected";
  return "shopee_to_seller.error.generic";
}

// toSellerErrorDetail is the raw backend text shown under the translated
// sentence. The coded 400's `message` is deliberately NOT surfaced: its
// translation already says the same thing, in Thai.
export function toSellerErrorDetail(error) {
  const data = error && error.response ? error.response.data : null;
  if (!data || typeof data !== "object") return "";
  if (errorBodyCode(data) === "category_required") return "";
  const raw = data.Cause || data.cause || data.Msg || data.msg;
  return raw === null || raw === undefined ? "" : String(raw).trim();
}

// toSellerWarnings pulls `warnings` out of a to-seller response.
//
// BOTH runs carry it: the dry run at :186-196 (wms_unmatched — a SKU whose code
// matches no wms_product.man_pn, which the operator wants to see BEFORE
// confirming, not after) and the real run at :408-415 (also
// image_import_failed). The preview block is fed from the same field either
// way, so one helper serves both.
export function toSellerWarnings(data) {
  if (!data || typeof data !== "object") return [];
  const list = data.warnings || data.Warnings;
  if (!Array.isArray(list)) return [];
  return list.filter((w) => w && typeof w === "object");
}

// --------------------------------------------------------------------------
// "open this listing on Shopee" (#284)
// --------------------------------------------------------------------------

// SHOPEE_SELLER_PRODUCT_URL_BASE is Shopee Seller Centre's per-product page.
//
// .co.th, hard-coded: every shop this Backoffice syncs is a Thai shop (the
// bo_seller rows are TH, and get_shop_info returns region "TH"). A region-aware
// base would need a region the results table does not have and would produce a
// silently wrong link for the one case it is meant to handle, so the constant
// stays visible here instead.
export const SHOPEE_SELLER_PRODUCT_URL_BASE = "https://seller.shopee.co.th/portal/product/";

// shopeeSellerProductUrl builds the Seller Centre link for one Shopee item id,
// or "" when the id is not a positive integer.
//
// "" IS THE IMPORTANT CASE. The results table of a bulk sync contains rows the
// operator needs to look at precisely because something went wrong with them, and
// an <a href=""> on such a row reloads the Backoffice — losing the job they were
// reading. So the caller renders plain text whenever this returns "", and every
// shape that is not a clean positive integer lands there: "", null, 0, a negative
// number, "12.5", "12abc", 1e21 (whose String() is "1e+21"), and a float.
//
// Item ids exceed 2^53 on Shopee, so a STRING id is validated with a regular
// expression and passed through untouched rather than being sent through Number()
// — which would quietly round it and link to a listing that does not exist.
export function shopeeSellerProductUrl(itemId) {
  if (itemId === null || itemId === undefined || typeof itemId === "boolean") return "";
  if (typeof itemId === "number") {
    if (!isFinite(itemId) || itemId <= 0 || Math.floor(itemId) !== itemId) return "";
    // Beyond 1e21 JS renders a number in exponential form, which is not an id.
    const asText = String(itemId);
    if (!/^[0-9]+$/.test(asText)) return "";
    return SHOPEE_SELLER_PRODUCT_URL_BASE + asText;
  }
  // STRINGS ONLY past this point. Anything else that happens to stringify into
  // digits is not an id: [931001] becomes "931001" under String(), so a row whose
  // item_id arrived as a one-element array would get a link built from a value
  // the contract never promised to accept ("a positive integer or a numeric
  // string"). An object already failed the regex; an array did not.
  if (typeof itemId !== "string") return "";
  const text = itemId.trim();
  if (!/^[0-9]+$/.test(text)) return "";
  // "0" and "000" are ids no listing has; they come from an empty column read as
  // a number, not from Shopee.
  if (/^0+$/.test(text)) return "";
  return SHOPEE_SELLER_PRODUCT_URL_BASE + text;
}

// rejectsFromError pulls `rejects` out of a 422 body, or [] when there is none.
export function rejectsFromError(error) {
  const data = error && error.response ? error.response.data : null;
  if (!data) return [];
  const list = data.rejects || data.Rejects;
  return Array.isArray(list) ? list : [];
}
